import { DetailList, Loading } from '@orobix/orobix-fe-components'
import { differenceInHours } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { useParams } from 'react-router-dom'

import { OptimizationProcess } from '../../types'
import { get } from '../../utils/api'
import { preferExample } from '../../utils/backend'
import { formatDateTime } from '../../utils/date'
import { getOptimizationProcessStatus } from '../../utils/optimizationProcesses'
import { withTryAgainErrorBoundary } from '../TryAgainErrorBoundary/TryAgainErrorBoundary'

interface RouteParams {
  optimizationProcessId: string
}

const useFetchOptimizationProcess = (optimizationProcessId: number) => {
  const options = {
    headers: preferExample(optimizationProcessId),
  }

  return useQuery(
    ['optimization-process', optimizationProcessId],
    () =>
      get<OptimizationProcess>(
        `/optimization-processes/${optimizationProcessId}/`,
        options
      ),
    {
      retry: false,
      useErrorBoundary: true,
    }
  )
}

export const OptimizationProcessAside = withTryAgainErrorBoundary(() => {
  const { t } = useTranslation()
  const { optimizationProcessId } = useParams<RouteParams>()

  const { data: optimizationProcess } = useFetchOptimizationProcess(
    Number(optimizationProcessId)
  )

  return optimizationProcess ? (
    <>
      <b>
        {t('Optimization process #{{ optimizationProcessId }}', {
          optimizationProcessId: optimizationProcess.id,
        })}
      </b>
      <DetailList
        list={[
          {
            title: t('Id'),
            value: optimizationProcess.id,
          },
          {
            title: t('Status'),
            value: getOptimizationProcessStatus(optimizationProcess.status),
          },
          {
            title: t('Created on'),
            value: formatDateTime(optimizationProcess.createdAt),
          },
          ...(optimizationProcess.startedAt
            ? [
                {
                  title: t('Started on'),
                  value: formatDateTime(optimizationProcess.startedAt),
                },
              ]
            : []),
          ...(optimizationProcess.stoppedAt
            ? [
                {
                  title: t('Completed on'),
                  value: formatDateTime(optimizationProcess.stoppedAt),
                },
              ]
            : []),
          ...(optimizationProcess.startedAt && optimizationProcess.stoppedAt
            ? [
                {
                  title: t('Duration'),
                  value: `${differenceInHours(
                    new Date(optimizationProcess.startedAt),
                    new Date(optimizationProcess.stoppedAt)
                  )}h`,
                },
              ]
            : []),
        ]}
      />
    </>
  ) : (
    <Loading />
  )
})

OptimizationProcessAside.displayName = 'OptimizationProcessAside'
